import {storeToRefs} from 'pinia';
import type {RouteLocationNormalized} from 'vue-router';
import {
    defineNuxtRouteMiddleware,
    useGuestHost,
    navigateTo,
    useAuth,
    useDataTracking,
    useError,
    useExtractQueryParametersAsSearchParams,
} from '#imports';
import {FetchError} from '@api';
import {useInstantConversationStore} from '~/store/instantConversation.store';

/**
 * This middleware handles the finalization of a draft (unlogged) instant conversation into a standard instant
 * conversation. It then redirects to messenger to display that new conversation.
 *
 * Note: the finalization is idempotent.
 */
export default defineNuxtRouteMiddleware(async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
    const host = useGuestHost();

    // Avoid running the middleware twice when facing an error
    if (useError().value) {
        return;
    }

    // Prevents infinite redirection loop
    if (to.fullPath === from.redirectedFrom?.fullPath) {
        return navigateTo(host, {external: true});
    }

    const {track} = useDataTracking();
    const queryParams = useExtractQueryParametersAsSearchParams(to);
    const action = queryParams.get('action');
    const draftConversationId = queryParams.get('draftConversationId');
    const freelancerId = queryParams.get('freelancerId');
    const searchEventId = queryParams.get('searchEventId');
    const {getSelectedIdentity, loggedIn, user} = storeToRefs(useAuth());
    const {finalizeDraftInstantConversation} = useInstantConversationStore();

    if (!loggedIn.value || action !== 'saveInstantConversation' || !draftConversationId || !getSelectedIdentity.value || !user.value) {
        return;
    }

    const clientIdentityId =
        getSelectedIdentity.value.identityType === 'CLIENT'
            ? getSelectedIdentity.value.id
            : user.value.identities.find((identity) => identity.identityType === 'CLIENT')?.id;

    if (!clientIdentityId) {
        return;
    }

    let conversationId: string | undefined;
    try {
        conversationId = await finalizeDraftInstantConversation({
            draftConversationId,
            clientIdentityId,
        });
    } catch (error: unknown) {
        const profilePath = to.path;
        return navigateTo(
            {
                path: profilePath,
                query: {error: error instanceof FetchError && error.statusCode === 429 ? 'instant-conversation-limit-reached' : 'generic'},
            },
            {replace: true},
        );
    }

    await track('instant_conversation_started', {
        freelancer_id: freelancerId ?? 'unknown',
        conversation_status: 'message_sent',
        draft_conversation_id: draftConversationId,
        conversation_id: conversationId,
        search_id: searchEventId ?? undefined,
    });

    return navigateTo(`${host}/messages/${conversationId}`, {external: true});
});
